import { motion } from 'framer-motion';
import Footer from '../components/Footer';

const MissionVisionPage = () => {
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        duration: 0.8,
        staggerChildren: 0.2
      }
    }
  };

  const itemVariants = {
    hidden: { y: 30, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: { duration: 0.6 }
    }
  };

  return (
    <div>
      {/* Hero Section with Background Video */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0 z-0">
          <video
            autoPlay
            loop
            muted
            playsInline
            className="w-full h-full object-cover"
          >
            <source src="/images/hero.m4v" type="video/mp4" />
            Your browser does not support the video tag.
          </video>
          <div className="absolute inset-0 bg-gradient-to-t from-tev-green/90 via-tev-green/70 to-transparent"></div>
        </div>
        
        <div className="relative z-10 text-center px-4 py-32">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="max-w-4xl mx-auto"
          >
            <h1 className="text-4xl md:text-6xl font-bold text-white mb-6 leading-tight">
              Our Mission, Vision & Values
            </h1>
            <p className="text-xl md:text-2xl text-white/90 max-w-3xl mx-auto opacity-90">
              Guiding principles that drive our commitment to food security across Africa
            </p>
          </motion.div>
        </div>
      </section>

      {/* Mission & Vision Content */}
      <section className="py-20 bg-gradient-to-b from-gray-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-12 mb-16">
            
            {/* Mission Section */}
            <motion.div 
              variants={itemVariants}
              className="bg-gradient-to-br from-tev-green to-tev-green-light p-8 rounded-2xl shadow-xl"
            >
              <div className="flex items-center mb-6">
                <div className="bg-white p-4 rounded-full mr-4">
                  <i className="fas fa-bullseye text-tev-green text-3xl"></i>
                </div>
                <h3 className="text-3xl font-bold text-white">Our Mission</h3>
              </div>
              <p className="text-lg text-white leading-relaxed mb-6">
                To leverage our resources and capabilities to contribute towards food security at local, national and regional 
                level, in a manner that is sustainable and impactful.
              </p>
              
              {/* Mission Image */}
              <div className="relative mt-6 rounded-xl overflow-hidden">
                <img 
                  src="/images/who we are.jpg" 
                  alt="Our Mission in Action" 
                  className="w-full h-48 object-cover rounded-xl"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-tev-green/30 to-transparent rounded-xl"></div>
              </div>
            </motion.div>
            
            {/* Vision Section */}
            <motion.div 
              variants={itemVariants}
              className="bg-gradient-to-br from-blue-600 to-indigo-600 p-8 rounded-2xl shadow-xl"
            >
              <div className="flex items-center mb-6">
                <div className="bg-white p-4 rounded-full mr-4">
                  <i className="fas fa-eye text-blue-600 text-3xl"></i>
                </div>
                <h3 className="text-3xl font-bold text-white">Our Vision</h3>
              </div>
              <p className="text-lg text-white leading-relaxed mb-6">
                To attain scale and market leadership in our value chains, which would allow us to contribute effectively and meaningfully to the realization 
                of a better world for our communities.
              </p>
              
              {/* Vision Image */}
              <div className="relative mt-6 rounded-xl overflow-hidden">
                <img 
                  src="/images/AFRI OILS.jpg" 
                  alt="Our Vision for Africa" 
                  className="w-full h-48 object-cover rounded-xl"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-blue-600/30 to-transparent rounded-xl"></div>
              </div>
            </motion.div>
          </div>
          
          {/* Core Values Section */}
          <motion.div
            variants={itemVariants}
            initial="hidden"
            animate="visible"
            className="mt-16"
          >
            <div className="text-center mb-12">
              <h2 className="text-4xl font-bold text-gray-900 mb-8">Our Core Values</h2>
              <p className="text-lg text-gray-700 mb-6">Our core values drive everything we do. They are:</p>
            </div>
            
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {[
                {
                  title: "Respect & Support",
                  description: "We respect, support and value our people",
                  icon: "Users",
                  image: "/images/partner.jpg"
                },
                {
                  title: "Excellence",
                  description: "Going the extra mile for customers and partners",
                  icon: "Award",
                  image: "/images/_MG_5830.jpg"
                },
                {
                  title: "Innovation",
                  description: "Thinking outside the box in all we do",
                  icon: "Lightbulb",
                  image: "/images/_MG_5876.jpg"
                },
                {
                  title: "Community Care",
                  description: "Caring for communities and natural environment",
                  icon: "Heart",
                  image: "/images/comm.jpg"
                },
                {
                  title: "Long-term Value",
                  description: "Delivering sustainable long-term value",
                  icon: "TrendingUp",
                  image: "/images/_MG_5901.jpg"
                },
                {
                  title: "Integrity",
                  description: "Leadership and integrity in all dealings",
                  icon: "Shield",
                  image: "/images/int.jpg"
                }
              ].map((value, index) => (
                <motion.div
                  key={index}
                  variants={itemVariants}
                  className="bg-white p-6 rounded-2xl shadow-lg hover:shadow-xl transition-shadow duration-300"
                >
                  {/* Value Image */}
                  <div className="relative mb-4 rounded-xl overflow-hidden h-32">
                    <img 
                      src={value.image} 
                      alt={value.title} 
                      className="w-full h-full object-cover rounded-xl"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-gray-200/50 to-transparent rounded-xl"></div>
                  </div>
                  
                  <div className="flex items-start">
                    <div className="flex-shrink-0 mr-4">
                      <div className="bg-gradient-to-r from-tev-green to-tev-green-light p-3 rounded-full">
                        <i className={`fas fa-${value.icon} text-white text-xl`}></i>
                      </div>
                    </div>
                    <div>
                      <h4 className="text-xl font-bold text-gray-900 mb-2">{value.title}</h4>
                      <p className="text-gray-700 leading-relaxed">{value.description}</p>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default MissionVisionPage;
